import { ElementRef, Injectable } from '@angular/core';

import { ReplaySubject, Subject } from 'rxjs';

import { Comment, CommentTarget } from 'app/providers/bridge-api/dashboard.service';
import { CommentDataChange } from './chart-report/chart-report-abstract/chart-report-abstract';

export type ExpansionPanelType = 'COMMENTS' | 'INSIGHTS';
export interface ExpansionPanelShowEventData {
  widgetId: string;
  widgetContainer: ElementRef;
  panelType: ExpansionPanelType;
  panelData?: any;
}

export interface ExpansionPanelHideEventData
  extends Pick<ExpansionPanelShowEventData, 'widgetId' | 'panelType'> {}

@Injectable({ providedIn: 'root' })
export class WidgetCommentsHandlerService {
  private commentsSubject = new ReplaySubject<Comment[]>(1);
  private visibleCommentsSubject = new ReplaySubject<Comment[]>(1);
  private commentTargetSubject = new ReplaySubject<CommentTarget>(1);
  private commentChangesSubject = new ReplaySubject<Map<string, CommentDataChange>>(1);

  private showExpansionPaneSubject = new Subject<ExpansionPanelShowEventData>();
  private hideExpansionPanelSubject = new Subject<ExpansionPanelHideEventData>();

  comments$ = this.commentsSubject.asObservable();
  visibleComments$ = this.visibleCommentsSubject.asObservable();
  commentTarget$ = this.commentTargetSubject.asObservable();
  commentChanges$ = this.commentChangesSubject.asObservable();

  showExpansionPanel$ = this.showExpansionPaneSubject.asObservable();
  hideExpansionPanel$ = this.hideExpansionPanelSubject.asObservable();

  constructor() {}

  setComments(comments: Comment[], visibleComments?: Comment[]) {
    const safeComments = [...comments];
    safeComments.sort((c1, c2) => {
      const ret = c1.target.pointId.localeCompare(c2.target.pointId);
      if (ret) {
        return ret;
      }

      const isFirstVisible = visibleComments?.includes(c1);
      const isSecondVisible = visibleComments?.includes(c2);
      if (!isFirstVisible && isSecondVisible) {
        return 1;
      }
      if (isFirstVisible && !isSecondVisible) {
        return -1;
      }

      const isFirstPinned = c1.titlePinned || c1.summaryPinned;
      const isSecondPinned = c2.titlePinned || c2.summaryPinned;
      if (isFirstPinned && !isSecondPinned) {
        return -1;
      }
      if (!isFirstPinned && isSecondPinned) {
        return 1;
      }

      const c1Date = new Date(c1.modifiedAt ?? c1.createdAt);
      const c2Date = new Date(c2.modifiedAt ?? c2.createdAt);

      return c2Date.getTime() - c1Date.getTime();
    });
    this.commentsSubject.next(safeComments);
    if (visibleComments) {
      this.setVisibleComments(visibleComments);
    }
  }

  setVisibleComments(comments: Comment[] = []) {
    const safeComments = [...comments];
    this.visibleCommentsSubject.next(safeComments);
  }

  setCommentTarget(target: CommentTarget) {
    this.commentTargetSubject.next(target);
  }

  setCommentChanges(commentChanges: Map<string, CommentDataChange>) {
    this.commentChangesSubject.next(commentChanges);
  }

  showExpansionPanel(event: ExpansionPanelShowEventData) {
    this.showExpansionPaneSubject.next(event);
  }

  hideExpansionPanel(event: ExpansionPanelHideEventData) {
    this.hideExpansionPanelSubject.next(event);
  }
}
